import { Link, router, usePage } from "@inertiajs/react";
import { Menu, X } from "lucide-react";
import type { ReactNode } from "react";
import { useState } from "react";

import type { User } from "@/serializers";

import { Logo } from "./Logo";

interface HeaderProps {
  currentUser: User;
}

interface NavLinkProps {
  href: string;
  active?: boolean;
  external?: boolean;
  method?: "delete";
  onClick?: () => void;
  children: ReactNode;
}

// Pixel nav link with sfruby.com's growing ruby underline on hover.
function NavLink({
  href,
  active = false,
  external = false,
  method,
  onClick,
  children,
}: NavLinkProps) {
  const className =
    "px-font text-px10 text-ink group relative py-1 transition-colors";
  const underline = (
    <span
      className={`bg-ruby-500 absolute -bottom-1 left-0 h-1 transition-all duration-300 ${
        active ? "w-full" : "w-0 group-hover:w-full"
      }`}
    />
  );

  if (external) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className={className}
        onClick={onClick}
      >
        {children}
        {underline}
      </a>
    );
  }

  return (
    <Link
      href={href}
      method={method}
      as={method ? "button" : undefined}
      className={`${className} ${method ? "cursor-pointer" : ""}`}
      onClick={onClick}
    >
      {children}
      {underline}
    </Link>
  );
}

// Stacked pixel link for the mobile drawer.
function MobileLink({
  href,
  external = false,
  method,
  onClick,
  children,
}: NavLinkProps) {
  const className = "px-font text-px10 text-ink hover:text-ruby-600 block py-3";

  if (external) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className={className}
        onClick={onClick}
      >
        {children}
      </a>
    );
  }

  return (
    <Link
      href={href}
      method={method}
      as={method ? "button" : undefined}
      className={`${className} ${method ? "w-full cursor-pointer text-left" : ""}`}
      onClick={onClick}
    >
      {children}
    </Link>
  );
}

export function Header({ currentUser }: HeaderProps) {
  const { url } = usePage();
  const { primary_cfp } = usePage().props;

  const cfp_closed = primary_cfp?.is_closed ?? true;

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const closeMenu = () => setIsMenuOpen(false);

  return (
    <header className="bg-cloud-50 border-ink sticky top-0 z-10 border-b-4">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-20 items-center justify-between">
          <div className="flex items-center">
            {url === "/" ? (
              <Logo />
            ) : (
              <Link
                href="/"
                className="flex items-center hover:opacity-85"
                prefetch
              >
                <Logo />
              </Link>
            )}
          </div>

          {currentUser && (
            <div className="lg:hidden print:hidden">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                aria-label="Toggle menu"
                aria-expanded={isMenuOpen}
                className="border-ink text-ink hover:bg-cloud-100 inline-flex h-10 w-10 items-center justify-center border-2 bg-white transition-colors"
              >
                <span className="sr-only">Open main menu</span>
                {isMenuOpen ? (
                  <X className="block h-5 w-5" />
                ) : (
                  <Menu className="block h-5 w-5" />
                )}
              </button>
            </div>
          )}

          {currentUser && (
            <nav className="hidden items-center justify-end space-x-8 lg:flex print:hidden">
              {!cfp_closed && (
                <NavLink
                  href="/proposals/new"
                  active={url === "/proposals/new"}
                >
                  Submit proposal
                </NavLink>
              )}

              <NavLink href="/proposals" active={url === "/proposals"}>
                My proposals
              </NavLink>

              <NavLink href="https://sfruby.com" external>
                Conf 2026
              </NavLink>

              {currentUser.is_reviewer && (
                <Link
                  href="/evaluations"
                  className="btn btn-primary px-4 py-2.5"
                  title="Review"
                >
                  Review
                </Link>
              )}

              <NavLink
                href="/auth/sign_out"
                method="delete"
                onClick={() => router.clearHistory()}
              >
                Sign out
              </NavLink>
            </nav>
          )}
        </div>
      </div>

      {currentUser && (
        <div
          className={`border-ink bg-white lg:hidden print:hidden ${
            isMenuOpen ? "block border-b-4" : "hidden"
          }`}
        >
          <nav className="flex flex-col gap-1 px-4 py-4">
            <MobileLink href="/" onClick={closeMenu}>
              Home
            </MobileLink>
            {!cfp_closed && (
              <MobileLink href="/proposals/new" onClick={closeMenu}>
                Submit proposal
              </MobileLink>
            )}
            <MobileLink href="/proposals" onClick={closeMenu}>
              My proposals
            </MobileLink>
            <MobileLink href="https://sfruby.com" external onClick={closeMenu}>
              Conf 2026
            </MobileLink>
            {currentUser.is_reviewer && (
              <Link
                href="/evaluations"
                onClick={closeMenu}
                className="btn btn-primary mt-1 inline-flex self-start px-4 py-2.5"
                title="Review"
              >
                Review
              </Link>
            )}
            <MobileLink
              href="/auth/sign_out"
              method="delete"
              onClick={() => {
                closeMenu();
                router.clearHistory();
              }}
            >
              Sign out
            </MobileLink>
          </nav>
        </div>
      )}
    </header>
  );
}
